/**
 * What each management port is for (the owner, 2026-10-16; ADR 0165): a
 * tech's connections to a client are a `network` activity per service. The
 * API keeps each port's (`network_port_services`, every port's, defaults
 * included) and each activity's (`details.service`); these are their names,
 * as the API's `network_services.py` gives them.
 */
import type { Activity, AppSettings } from "../api/types";

export type NetworkService = AppSettings["network_port_services"][string];

const LABELS: Record<NetworkService, string> = {
  ssh: "SSH",
  rdp: "RDP",
  firewall_management: "Firewall management",
  server_management: "Server management (iDRAC/iLO)",
  web_https: "Web (HTTPS)",
  other: "Other",
};

/** Every service, in the order the drop-down offers them. */
export const NETWORK_SERVICES: readonly NetworkService[] = [
  "ssh",
  "rdp",
  "firewall_management",
  "server_management",
  "web_https",
  "other",
];

export function serviceLabel(service: NetworkService): string {
  return LABELS[service];
}

function isService(value: unknown): value is NetworkService {
  return typeof value === "string" && Object.hasOwn(LABELS, value);
}

/**
 * A network activity's service, as its details keep it. Null for any other
 * source's, and for one made per client before ADR 0165, which has none.
 */
export function networkServiceOf(activity: Pick<Activity, "source" | "details">): NetworkService | null {
  if (activity.source !== "network") {
    return null;
  }
  const service = activity.details.service;
  return isService(service) ? service : null;
}

/**
 * How a network activity was reached, as its details keep it (`via`; ADR
 * 0180), in the words the client audit uses too: "via VPN", or "from the
 * office and via VPN" for both. Null for office work alone, which looks as it
 * always did, for one made before, and for any other source's.
 */
export function networkRouteOf(activity: Pick<Activity, "source" | "details">): string | null {
  if (activity.source !== "network") {
    return null;
  }
  const via: unknown = activity.details.via;
  if (!Array.isArray(via) || !via.includes("vpn")) {
    return null;
  }
  return via.includes("office") ? "from the office and via VPN" : "via VPN";
}
